/**
 * PlanBundlePdf — takes a finished AI meal plan and produces a single
 * downloadable A4 PDF that stitches together the household roster,
 * shopping list, weekly schedule, and one recipe card per suggested
 * meal (fetched from /tools/recipe-card-ai in parallel).
 *
 * We also expose lightweight per-section PDF exports (`downloadShoppingPdf`,
 * `downloadSchedulePdf`) so the operator can grab just one section without
 * waiting for every recipe card to render.
 *
 * Data-testids: fp-bundle-pdf, fp-pdf-shopping, fp-pdf-schedule.
 */

import React, { useContext, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { FileText, Loader2, ClipboardList, CalendarDays, BookOpen } from 'lucide-react';

// -------------------- Helpers ---------------------------------------------

const A4_W_MM = 210;
const A4_H_MM = 297;
const MARGIN_MM = 10;

async function renderElementToPdf(el, filename, pdf = null) {
  // Turn any DOM node into one or more PDF pages, appending to `pdf` if
  // given so we can chain multiple sections into a single document.
  const canvas = await html2canvas(el, {
    background: '#ffffff', scale: 2, useCORS: true,
  });
  const doc = pdf || new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const imgWmm = A4_W_MM - MARGIN_MM * 2;
  const pageContentH = A4_H_MM - MARGIN_MM * 2;
  const imgHmm = (canvas.height / canvas.width) * imgWmm;

  const dataUrl = canvas.toDataURL('image/png');
  if (imgHmm <= pageContentH) {
    if (pdf && pdf.__hasContent) doc.addPage();
    doc.addImage(dataUrl, 'PNG', MARGIN_MM, MARGIN_MM, imgWmm, imgHmm);
    doc.__hasContent = true;
  } else {
    // Slice the tall canvas into A4-sized pages.
    const sliceHpx = (pageContentH / imgHmm) * canvas.height;
    let yStart = 0;
    let first = true;
    while (yStart < canvas.height) {
      const h = Math.min(sliceHpx, canvas.height - yStart);
      const slice = document.createElement('canvas');
      slice.width = canvas.width; slice.height = h;
      slice.getContext('2d').drawImage(
        canvas, 0, yStart, canvas.width, h, 0, 0, canvas.width, h,
      );
      const sliceH_mm = (h / canvas.width) * imgWmm;
      if (!first || (pdf && pdf.__hasContent)) doc.addPage();
      doc.addImage(slice.toDataURL('image/png'), 'PNG', MARGIN_MM, MARGIN_MM, imgWmm, sliceH_mm);
      doc.__hasContent = true;
      first = false;
      yStart += h;
    }
  }

  if (!pdf && filename) {
    doc.save(filename);
  }
  return doc;
}

// -------------------- The bundled PDF button ------------------------------

export default function PlanBundlePdf({ plan, members, days, mealsPerDay, machine, calc }) {
  const { API } = useContext(AuthContext);
  const [busy, setBusy]     = useState(false);
  const [status, setStatus] = useState('');
  const shellRef = useRef(null);

  if (!plan) return null;

  const bundle = async () => {
    setBusy(true);
    setStatus('Fetching recipe cards…');
    try {
      // Fetch recipe cards for every meal in parallel.
      const cards = await Promise.all(
        (plan.meals || []).map((m) =>
          axios
            .post(`${API}/tools/recipe-card-ai`, {
              meal_name: m.name, servings: m.servings || 4,
            })
            .then((r) => r.data)
            .catch(() => null),   // one bad card shouldn't kill the whole bundle
        ),
      );

      setStatus('Composing PDF…');
      // Render the hidden shell that ties everything together, then let
      // html2canvas do its thing. The shell is inside an off-screen wrapper
      // in the DOM so react has already committed it.
      const shell = shellRef.current;
      if (!shell) throw new Error('PDF render shell missing');

      // Inject the cards after the fetch resolves, then wait one frame so
      // React commits the DOM before we snapshot.
      shell.dataset.cards = JSON.stringify(cards.filter(Boolean));
      window.dispatchEvent(new Event('fp-pdf-cards-ready'));
      await new Promise((res) => requestAnimationFrame(res));

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      pdf.__hasContent = false;
      // Each direct child of the shell becomes its own page.
      for (const child of Array.from(shell.children)) {
        // Skip zero-height children (e.g. an empty recipe placeholder).
        if (child.offsetHeight < 20) continue;
        await renderElementToPdf(child, null, pdf);
      }
      pdf.save('household-meal-plan.pdf');
      toast.success('Bundled plan PDF downloaded.');
    } catch (e) {
      toast.error(`Bundle PDF failed: ${e?.message || e}`);
    } finally {
      setBusy(false);
      setStatus('');
    }
  };

  return (
    <>
      <Button
        type="button" size="sm"
        onClick={bundle}
        disabled={busy}
        data-testid="fp-bundle-pdf"
      >
        {busy
          ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> {status || 'Working…'}</>
          : <><FileText className="w-3.5 h-3.5 mr-1" /> Download Full Plan PDF</>}
      </Button>

      {/* Off-screen render target — kept in the DOM so html2canvas can
          snapshot exact page-sized sections. */}
      <div
        style={{
          position: 'fixed', left: '-10000px', top: 0, width: '190mm',
          background: '#ffffff', color: '#000', fontFamily: '"Helvetica Neue", Arial, sans-serif',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      >
        <div ref={shellRef}>
          <PdfShell
            plan={plan}
            members={members}
            days={days}
            mealsPerDay={mealsPerDay}
            machine={machine}
            calc={calc}
          />
        </div>
      </div>
    </>
  );
}

// -------------------- Per-section shortcut buttons ------------------------

export function PlanSectionPdfButtons({ plan, members, days, mealsPerDay, machine, calc }) {
  const shoppingRef = useRef(null);
  const scheduleRef = useRef(null);
  const rosterRef   = useRef(null);
  const [busy, setBusy] = useState('');

  if (!plan) return null;

  const download = async (which) => {
    setBusy(which);
    try {
      const el = which === 'shop' ? shoppingRef.current
              : which === 'sched' ? scheduleRef.current
              : rosterRef.current;
      if (!el) throw new Error('render target missing');
      const filename =
        which === 'shop'  ? 'shopping-list.pdf' :
        which === 'sched' ? 'production-schedule.pdf' :
                            'household-roster.pdf';
      await renderElementToPdf(el, filename);
      toast.success('PDF downloaded.');
    } catch (e) {
      toast.error(`PDF failed: ${e?.message || e}`);
    } finally { setBusy(''); }
  };

  return (
    <>
      <Button
        type="button" size="sm" variant="outline"
        onClick={() => download('roster')} disabled={!!busy}
        data-testid="fp-pdf-roster"
      >
        {busy === 'roster'
          ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          : <BookOpen className="w-3.5 h-3.5 mr-1" />}
        Roster PDF
      </Button>
      <Button
        type="button" size="sm" variant="outline"
        onClick={() => download('shop')} disabled={!!busy}
        data-testid="fp-pdf-shopping"
      >
        {busy === 'shop'
          ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          : <ClipboardList className="w-3.5 h-3.5 mr-1" />}
        Shopping PDF
      </Button>
      <Button
        type="button" size="sm" variant="outline"
        onClick={() => download('sched')} disabled={!!busy}
        data-testid="fp-pdf-schedule"
      >
        {busy === 'sched'
          ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          : <CalendarDays className="w-3.5 h-3.5 mr-1" />}
        Schedule PDF
      </Button>

      {/* Hidden per-section render targets. */}
      <div
        style={{
          position: 'fixed', left: '-10000px', top: 0, width: '190mm',
          background: '#ffffff', color: '#000', fontFamily: '"Helvetica Neue", Arial, sans-serif',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      >
        <div ref={rosterRef}><RosterPage members={members} days={days} mealsPerDay={mealsPerDay} machine={machine} calc={calc} /></div>
        <div ref={shoppingRef}><ShoppingPage plan={plan} /></div>
        <div ref={scheduleRef}><SchedulePage plan={plan} /></div>
      </div>
    </>
  );
}

// -------------------- PDF Section Renderers -------------------------------

function PdfShell({ plan, members, days, mealsPerDay, machine, calc }) {
  // Cards get injected via the shell's dataset by the bundler; read them
  // out on each render. We don't need React re-render synchronisation
  // because the bundler pushes an event that forces the parent to update.
  const [cards, setCards] = React.useState([]);
  React.useEffect(() => {
    const handler = () => {
      try {
        const raw = document.querySelector('[data-fp-pdf-shell]')?.dataset.cards;
        setCards(raw ? JSON.parse(raw) : []);
      } catch { setCards([]); }
    };
    window.addEventListener('fp-pdf-cards-ready', handler);
    return () => window.removeEventListener('fp-pdf-cards-ready', handler);
  }, []);

  return (
    <div data-fp-pdf-shell>
      <CoverPage plan={plan} days={days} mealsPerDay={mealsPerDay} machine={machine} />
      <RosterPage members={members} days={days} mealsPerDay={mealsPerDay} machine={machine} calc={calc} />
      <ShoppingPage plan={plan} />
      <SchedulePage plan={plan} />
      {cards.map((c, i) => <RecipePage key={i} card={c} />)}
    </div>
  );
}

const pageStyle = {
  padding: '15mm 12mm',
  minHeight: '270mm',
  boxSizing: 'border-box',
  color: '#000',
  fontSize: '11pt',
};

function CoverPage({ plan, days, mealsPerDay, machine }) {
  const totals = plan?.totals || {};
  return (
    <div style={{ ...pageStyle, textAlign: 'center', paddingTop: '60mm' }}>
      <div style={{ fontSize: '9pt', letterSpacing: 3, textTransform: 'uppercase', color: '#555' }}>
        Freeze Dry Academy
      </div>
      <div style={{ fontSize: '30pt', fontWeight: 900, marginTop: '8mm' }}>
        Household Meal Plan
      </div>
      <div style={{ fontSize: '11pt', color: '#333', marginTop: '4mm' }}>
        {days} days · {mealsPerDay} meals/day · {machine?.label || 'machine'}
      </div>
      <div style={{ marginTop: '24mm', fontSize: '11pt', color: '#111', lineHeight: 1.5 }}>
        {plan?.narrative}
      </div>
      {totals.raw_cost_usd ? (
        <div style={{ marginTop: '16mm', border: '1px solid #000', padding: '6mm', display: 'inline-block', textAlign: 'left' }}>
          <div><b>Raw ingredients:</b> ${Number(totals.raw_cost_usd).toLocaleString()}</div>
          <div><b>Ready-to-eat equivalent:</b> ${Number(totals.ready_meal_cost_usd).toLocaleString()}</div>
          <div><b>Estimated savings:</b> ${Number(totals.savings_usd).toLocaleString()} ({totals.savings_pct}%)</div>
        </div>
      ) : null}
      <div style={{ marginTop: '30mm', fontSize: '9pt', color: '#666' }}>
        {plan?.disclaimer}
      </div>
    </div>
  );
}

function RosterPage({ members = [], days, mealsPerDay, machine, calc }) {
  return (
    <div style={pageStyle}>
      <h2 style={{ fontSize: '18pt', fontWeight: 800, borderBottom: '1px solid #000', paddingBottom: '2mm' }}>
        Household roster
      </h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '4mm', fontSize: '10pt' }}>
        <thead>
          <tr style={{ background: '#eee' }}>
            <th style={cell()}>Name</th>
            <th style={cell()}>Age</th>
            <th style={cell()}>Gender</th>
            <th style={cell()}>Activity</th>
            <th style={cell('right')}>Kcal/day</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id}>
              <td style={cell()}>{m.name}</td>
              <td style={cell()}>{m.age}</td>
              <td style={cell()}>{m.gender}</td>
              <td style={cell()}>{m.activity}{m.pregnant ? ' · pregnant' : ''}{m.breastfeeding ? ' · breastfeeding' : ''}</td>
              <td style={cell('right')}>{Math.round(m.__kcal ?? 0).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {calc && (
        <div style={{ marginTop: '6mm', fontSize: '10pt' }}>
          <p><b>Coverage:</b> {days} days · {mealsPerDay} meals/day · {machine?.label}</p>
          <p><b>Total kcal window:</b> {calc.totalKcal?.toLocaleString()} kcal</p>
          <p><b>Total fresh food to source:</b> {calc.totalFreshKg?.toFixed(1)} kg</p>
          <p><b>Batches needed:</b> {calc.batches}</p>
        </div>
      )}
    </div>
  );
}

function ShoppingPage({ plan }) {
  const rows = plan?.shopping_list || [];
  const totals = plan?.totals || {};
  return (
    <div style={pageStyle}>
      <h2 style={{ fontSize: '18pt', fontWeight: 800, borderBottom: '1px solid #000', paddingBottom: '2mm' }}>
        Shopping list
      </h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '4mm', fontSize: '10pt' }}>
        <thead>
          <tr style={{ background: '#eee' }}>
            <th style={cell()}>Ingredient</th>
            <th style={cell('right')}>Qty (kg)</th>
            <th style={cell('right')}>Raw cost</th>
            <th style={cell('right')}>Ready-to-eat equiv.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={cell()}>
                {r.ingredient}
                {r.ready_meal_reference ? (
                  <div style={{ fontSize: '8pt', fontStyle: 'italic', color: '#555' }}>
                    vs. {r.ready_meal_reference}
                  </div>
                ) : null}
              </td>
              <td style={cell('right')}>{Number(r.quantity_kg || 0).toFixed(1)}</td>
              <td style={cell('right')}>${Number(r.raw_cost_usd || 0).toFixed(2)}</td>
              <td style={cell('right')}>${Number(r.ready_meal_equivalent_usd || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#f2f2f2', fontWeight: 700 }}>
            <td style={cell()}>Totals</td>
            <td style={cell()}></td>
            <td style={cell('right')}>${Number(totals.raw_cost_usd || 0).toFixed(2)}</td>
            <td style={cell('right')}>${Number(totals.ready_meal_cost_usd || 0).toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
      <p style={{ fontSize: '8pt', color: '#666', marginTop: '4mm' }}>
        Baseline: Walmart Great Value / US store-brand retail. Verify locally.
      </p>
    </div>
  );
}

function SchedulePage({ plan }) {
  const p = plan?.production_plan || {};
  return (
    <div style={pageStyle}>
      <h2 style={{ fontSize: '18pt', fontWeight: 800, borderBottom: '1px solid #000', paddingBottom: '2mm' }}>
        Production schedule
      </h2>
      <p style={{ fontSize: '10pt', color: '#333', marginTop: '3mm' }}>
        {p.batches} batches over {p.weeks} weeks.
      </p>
      <ol style={{ marginTop: '5mm', paddingLeft: '5mm', fontSize: '10pt', lineHeight: 1.5 }}>
        {(p.schedule || []).map((line, i) => (
          <li key={i} style={{ marginBottom: '2mm' }}>{line}</li>
        ))}
      </ol>
    </div>
  );
}

function RecipePage({ card }) {
  if (!card) return null;
  return (
    <div style={pageStyle}>
      <h2 style={{ fontSize: '16pt', fontWeight: 800, borderBottom: '1px solid #000', paddingBottom: '2mm' }}>
        {card.name}
      </h2>
      <p style={{ fontSize: '9pt', color: '#555', textTransform: 'uppercase', letterSpacing: 1 }}>
        Yields {card.servings} servings
        {card.prep_time_min ? ` · Prep ${card.prep_time_min} min` : ''}
        {card.cook_time_min ? ` · Cook ${card.cook_time_min} min` : ''}
      </p>
      {card.ingredients?.length > 0 && (
        <>
          <h3 style={sub()}>Ingredients</h3>
          <ul style={{ paddingLeft: '5mm', fontSize: '10pt' }}>
            {card.ingredients.map((it, i) => (
              <li key={i} style={{ marginBottom: '1mm' }}>
                <b>{it.quantity} {it.unit}</b> — {it.item}
              </li>
            ))}
          </ul>
        </>
      )}
      {card.instructions?.length > 0 && (
        <>
          <h3 style={sub()}>Instructions</h3>
          <ol style={{ paddingLeft: '5mm', fontSize: '10pt' }}>
            {card.instructions.map((s, i) => (
              <li key={i} style={{ marginBottom: '2mm' }}>{s}</li>
            ))}
          </ol>
        </>
      )}
      {card.freeze_dry_prep && <Section title="Freeze-dry prep" body={card.freeze_dry_prep} />}
      {card.rehydration     && <Section title="Rehydration"     body={card.rehydration} />}
      {card.storage         && <Section title="Storage"         body={card.storage} />}
      {card.nutrition_note  && (
        <p style={{ fontSize: '9pt', fontStyle: 'italic', color: '#555', marginTop: '4mm' }}>
          {card.nutrition_note}
        </p>
      )}
    </div>
  );
}

function Section({ title, body }) {
  return (
    <div style={{ marginTop: '3mm' }}>
      <h3 style={sub()}>{title}</h3>
      <p style={{ fontSize: '10pt', margin: 0 }}>{body}</p>
    </div>
  );
}

function cell(align = 'left') {
  return {
    border: '1px solid #666',
    padding: '2mm 3mm',
    textAlign: align,
    verticalAlign: 'top',
    fontSize: '10pt',
  };
}

function sub() {
  return {
    fontSize: '11pt', fontWeight: 700, marginTop: '4mm', marginBottom: '1mm',
    textTransform: 'uppercase', letterSpacing: 1, color: '#000',
  };
}
